import { AssetMetadata, AssetOwnerCategory } from '../models/Asset';
import React, { CSSProperties, useEffect, useRef, useState } from 'react';

import { AssetUploading } from '../hooks/useAssetStorage';
import AuthService from '../services/AuthService';
import Button from '@mui/material/Button';
import { CircularProgressWithValue } from './CircularProgressWithValue';
import Grid from '@mui/material/Unstable_Grid2';
import Typography from '@mui/material/Typography';

const FrameSize = { width: 200, height: 120 };

interface AssetCardProps {
  category: AssetOwnerCategory;
  asset: AssetUploading;
  onUploadCompleted: (assetMetadata: AssetMetadata) => void;
  onUploadCanceled: () => void;
  onUploadError: () => void;
  onEncodeCompleted: (assetMetadata: AssetMetadata) => void;
  onEncodeError: () => void;
}

export function AssetCard(props: AssetCardProps) {
  const [progress, setProgress] = useState(0);
  const checkTimerId = useRef(-1);
  const assetMetadata = useRef<AssetMetadata>(null);
  const userkey = AuthService.Userkey;

  useEffect(() => {
    if (!props.asset.uploadObservable) {
      return;
    }
    const subscription = props.asset.uploadObservable.subscribe({
      next: (progress) => {
        setProgress(progress.value);
      },
      error: (_) => {
        props.onUploadError();
        subscription.unsubscribe();
      },
      complete: () => {
        props.onUploadCompleted(props.asset.metadata);
        subscription.unsubscribe();
      }
    });
  }, [props.asset]);

  useEffect(() => {
    if (!props.asset.encodeObservable) {
      return;
    }

    const subscription = props.asset.encodeObservable.subscribe({
      next: (timerIdOrAssetMetadata) => {
        if (typeof timerIdOrAssetMetadata === 'number') {
          checkTimerId.current = timerIdOrAssetMetadata;
        } else {
          assetMetadata.current = timerIdOrAssetMetadata;
        }
      },
      error: (_) => {
        window.clearInterval(checkTimerId.current);
        props.onEncodeError();
        subscription.unsubscribe();
      },
      complete: () => {
        window.clearInterval(checkTimerId.current);
        props.onEncodeCompleted(assetMetadata.current);
        subscription.unsubscribe();
      }
    });
  }, [props.asset.encodeObservable]);

  const imageFrameStyle: CSSProperties = {
    width: `${FrameSize.width}px`,
    height: `${FrameSize.height}px`
  };

  if (!props.asset?.uploadObservable && !props.asset?.encodeObservable) {
    return null;
  }

  return (
    <div className="assetContainer" style={imageFrameStyle}>
      <div className="verticallyCentered">
        <Grid container direction="column" justifyContent="space-evenly">
          <Grid>
            <CircularProgressWithValue
              value={props.asset.uploadObservable ? progress : null}
            />
          </Grid>
          <Grid>
            {props.asset.uploadObservable ? (
              <Button
                size="small"
                variant="outlined"
                color="secondary"
                onClick={(event) => {
                  event.stopPropagation();
                  props.onUploadCanceled();
                }}
              >
                Cancel
              </Button>
            ) : (
              <Typography variant="caption">Processing...</Typography>
            )}
          </Grid>
        </Grid>
      </div>
    </div>
  );
}
